import styled from '@emotion/styled';
import {
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import { VerticalWidgetScrollerContainer } from 'components/atoms/VerticalWidgetScrollerContainer';
import { formatUnixTimestampSeconds } from 'helpers/formatHelpers';
import { useAnnouncementsFromApi } from 'hooks/useAnnouncements';
import { getUrls } from 'helpers/getUrlHelper';

const AnnouncementsWidget = () => {
  const { oneBaseUrl } = getUrls();
  const { announcements, isAnnouncementsLoading, announcementsError } =
    useAnnouncementsFromApi();

  const showFetchError =
    !isAnnouncementsLoading &&
    !!announcementsError &&
    announcements.length === 0;

  const sortedAnnouncements = announcements.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <AnnouncementsWrapper>
      <VerticalWidgetScrollerContainer>
        {isAnnouncementsLoading &&
          [1, 2, 3, 4, 5].map((item) => <StyledSkeleton key={item} />)}
        {showFetchError && (
          <ErrorMessage>
            <SvgIcon
              name={SvgNames.ErrorOutline}
              width={28}
              height={28}
              color={tokens.TINTS_ERROR_40}
            />
            <Typography size="small" align="center">
              Couldn&apos;t load announcements. Please try again later.
            </Typography>
          </ErrorMessage>
        )}
        {!isAnnouncementsLoading &&
          !showFetchError &&
          announcements.length === 0 && (
            <EmptyMessage>
              <Typography size="small">No announcements</Typography>
            </EmptyMessage>
          )}
        {!isAnnouncementsLoading &&
          !showFetchError &&
          sortedAnnouncements.length > 0 &&
          sortedAnnouncements.map((announcement) => (
            <AnnouncementItemWrapper key={`${announcement.announcementId}`}>
              <AnnouncementItem>
                <Typography>{announcement.title}</Typography>
                <Typography
                  size="extra_small"
                  color={tokens.TINTS_OFF_WHITE_20}>
                  {announcement.createdAt &&
                    formatUnixTimestampSeconds(announcement.createdAt)}
                </Typography>
              </AnnouncementItem>
              <div
                onClick={() => {
                  window.parent.postMessage(
                    {
                      type: 'COMMAND:CHANGE_URL',
                      source: 'Dashboard',
                      payload: {
                        url: `${oneBaseUrl}/notifications?announcements=true`,
                      },
                    },
                    '*',
                  );
                }}>
                <SvgIcon
                  name={SvgNames.ChevronRightCircle}
                  width={24}
                  height={24}
                  color={tokens.BRAND_HEROBLUE}
                />
              </div>
            </AnnouncementItemWrapper>
          ))}
      </VerticalWidgetScrollerContainer>
    </AnnouncementsWrapper>
  );
};

const AnnouncementItemWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${tokens.WHITE};
  border-radius: 8px;
  padding: 8px 12px;
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
`;

const StyledSkeleton = styled(Skeleton)`
  width: 100%;
  height: 66px;
  min-height: 66px;
`;

const EmptyMessage = styled.div`
  padding: 12px 0;
  text-align: center;
`;

const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  text-align: center;
`;

const AnnouncementItem = styled.div`
  box-sizing: border-box;
  gap: 4px;
  display: flex;
  flex-direction: column;
`;

const AnnouncementsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export { AnnouncementsWidget };
